import { useRef, useEffect } from 'react';
import NavArrow from './NavArrow';
import s from './LogoCarousel.module.scss';

// ── 로고 스텝 캐러셀(재사용 원본 1개) ─────────────────────────────────
//   배경/근거: Trust(인증·협력 기관)·Clients(주요 거래처) 두 섹션이 "단일행 + 동일 스텝 애니메이션 +
//   좌우 버튼"으로 동일해짐(사용자 지시 2026-07-31) → 캐러셀 로직을 원본 1개로 추출(복붙 금지).
//   동작: 슬라이드 1칸씩 "이동(ease) → 정지(pause) → 이동" 스텝. 로고 ×3 복제 + 중간 세트 시작 +
//     경계 무전환 점프로 seamless 무한 순환. 마우스오버 시 일시정지.
//   뷰포트 폭 = 슬롯 폭의 "정수배"로 런타임 스냅 → 정지 시 항상 온전한 카드만(잘림 0).
//     slot 폭·track gap 을 DOM 실측해 STEP·뷰포트폭 동적 계산 + resize 대응(고정 px 아님).
//   변경 금지 트리거: 슬롯/gap/화살표 크기 변경 시 measure()가 자동 반영(상수 수정 불필요).
const SPEED = 650;  // 1칸 이동(ms), ease
const DELAY = 2500; // 스텝 사이 정지(ms)
const ARROW = 56;   // NavArrow 폭(px)
const CAR_GAP = 28; // .carousel gap(px), 좌우 2곳

type Logo = { name: string; src: string };

export default function LogoCarousel({ logos }: { logos: Logo[] }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const advanceRef = useRef<(dir: number) => void>(() => {});
  const n = logos.length;
  const loop = [...logos, ...logos, ...logos]; // ×3 (seamless)

  useEffect(() => {
    const el = trackRef.current;
    const vp = viewportRef.current;
    const car = carouselRef.current;
    if (!el || !vp || !car) return;
    let idx = n;            // 중간 세트에서 시작
    let paused = false;
    let timer = 0 as unknown as ReturnType<typeof setTimeout>;
    let STEP = 0;

    const measure = () => {
      const slot = el.children[0] as HTMLElement | undefined;
      if (!slot) return;
      const slotW = slot.getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(el).gap) || 0;
      STEP = slotW + gap;
      const avail = car.clientWidth - 2 * ARROW - 2 * CAR_GAP;
      const k = Math.max(1, Math.floor((avail + gap) / STEP));
      vp.style.width = `${k * STEP - gap}px`;   // 마지막 슬롯이 경계에 정확히 맞물림
      vp.style.flex = '0 0 auto';
    };

    const place = (withTrans: boolean) => {
      el.style.transition = withTrans ? `transform ${SPEED}ms ease` : 'none';
      el.style.transform = `translate3d(${-idx * STEP}px,0,0)`;
    };
    const normalize = () => {
      if (idx >= n * 2) { idx -= n; place(false); void el.offsetHeight; }
      else if (idx < n) { idx += n; place(false); void el.offsetHeight; }
    };
    const advance = (dir: number) => { normalize(); idx += dir; place(true); };
    const scheduleNext = () => { clearTimeout(timer); timer = setTimeout(() => { if (!paused) advance(1); }, DELAY); };
    advanceRef.current = (dir: number) => { clearTimeout(timer); advance(dir); };

    measure();
    place(false);
    void el.offsetHeight;
    timer = setTimeout(() => { if (!paused) advance(1); }, DELAY);

    const onEnd = (e: TransitionEvent) => {
      if (e.propertyName !== 'transform') return;
      if (!paused) scheduleNext();
    };
    const enter = () => { paused = true; clearTimeout(timer); };
    const leave = () => { paused = false; scheduleNext(); };
    const onResize = () => { measure(); place(false); };
    el.addEventListener('transitionend', onEnd);
    el.addEventListener('mouseenter', enter);
    el.addEventListener('mouseleave', leave);
    window.addEventListener('resize', onResize);

    return () => {
      clearTimeout(timer);
      el.removeEventListener('transitionend', onEnd);
      el.removeEventListener('mouseenter', enter);
      el.removeEventListener('mouseleave', leave);
      window.removeEventListener('resize', onResize);
    };
  }, [n]);

  const nudge = (dir: number) => advanceRef.current(dir);

  return (
    <div className={s.carousel} ref={carouselRef}>
      <NavArrow dir="prev" onClick={() => nudge(-1)} />
      <div className={s.viewport} ref={viewportRef}>
        <div className={s.track} ref={trackRef}>
          {loop.map((p, i) => (
            <span className={s.slot} key={`${p.name}-${i}`} title={p.name}>
              <img src={p.src} alt={p.name} loading="lazy" />
            </span>
          ))}
        </div>
      </div>
      <NavArrow dir="next" onClick={() => nudge(1)} />
    </div>
  );
}
